<template>
  <aside class="w-full h-full flex flex-col px-2 sm:px-6 md:px-0">

    <!-- Advertisements -->
    <section v-reveal="{ delay: 0.15 }" class="card flex-1 p-5 xl:p-6 relative overflow-y-auto overflow-x-hidden scrollbar-hide max-h-[820px]">

      <div class="section-heading mb-5">
        <h2 class="section-title">Advertisements</h2>
        <span class="section-subtitle">( বিজ্ঞাপন )</span>
      </div>

      <div class="space-y-6">
        <!-- Static Publish Section -->
        <div class="group/ad">
          <h3 class="font-serif text-lg font-bold leading-snug text-gray-900">
            Publish Your Advertisement
          </h3>
          <p class="text-[13px] text-gray-500 mb-3">আপোনাৰ বিজ্ঞাপন প্ৰকাশ কৰক</p>
          <div class="relative w-full rounded-md overflow-hidden bg-gray-100">
            <a href="#" @click.prevent="openPublishModal" class="block">
              <img
                :src="publishImage"
                alt="Publish Ad"
                class="w-full aspect-[16/9] object-cover transition-transform duration-700 ease-out group-hover/ad:scale-105"
              />
            </a>
          </div>
          <p class="text-gray-600 text-sm mt-4 leading-relaxed">
            Want to showcase your film or event? Publish your advertisement
            with us and reach a wider audience.
          </p>
          <div class="mt-4">
            <button
              @click="openPublishModal"
              class="btn-cream w-full"
            >
              <i class="fa-solid fa-bullhorn text-xs"></i>
              Publish
            </button>
          </div>
        </div>

        <!-- Dynamic Ads -->
        <div v-for="ad in ads" :key="ad.id" class="border-t border-gray-100 pt-6 group/dynad">
          <h3 class="font-serif text-lg font-bold leading-snug mb-3 text-gray-900">
            {{ ad.title }}
          </h3>
          <div class="relative w-full rounded-md overflow-hidden bg-gray-100">
            <img
              :src="ad.image"
              :alt="ad.title"
              class="w-full h-40 md:h-48 object-cover transition-transform duration-700 ease-out group-hover/dynad:scale-105"
              @error="handleImageError"
            />
          </div>
          <p class="text-gray-600 text-[15px] mt-3 leading-relaxed">
            {{ ad.description }}
          </p>
          <p class="text-gray-400 font-medium text-xs mt-2 flex items-center gap-1.5">
            <i class="fa-regular fa-calendar"></i>
            <span>{{ formatDate(ad.publish_date) }}</span>
          </p>
        </div>
      </div>

      <!-- Ads Error -->
      <p v-if="adsError" class="text-brand-red-dark font-medium text-sm mt-4 text-center bg-red-50 p-3 rounded-md border border-red-100">
        {{ adsError }}
      </p>
    </section>

    <!-- Advertisement Section (teleported so the modal sits above the sticky navbar) -->
    <Teleport to="body">
      <AdvertisementForm v-if="showAdForm" @close="showAdForm = false" />
    </Teleport>
  </aside>
</template>

<script setup>
import { ref, onMounted } from "vue";
import Swal from 'sweetalert2';
import publishImage from "@/assets/images/publish.jpg";
import AdvertisementForm from './AdvertisementForm.vue';

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL;
const FILE_BASE_URL = process.env.VUE_APP_FILE_BASE_URL;

if (!API_BASE_URL || !FILE_BASE_URL) {
  console.error(
    "Missing .env variables! Add VUE_APP_API_BASE_URL and VUE_APP_FILE_BASE_URL"
  );
}

const ads = ref([]);
const adsError = ref("");
const showAdForm = ref(false);

const resolveImage = (img) => {
  if (!img) return `${FILE_BASE_URL}/images/default.png`;
  if (typeof img === "string" && img.startsWith("data:")) return img;
  if (typeof img === "string" && (img.startsWith("http://") || img.startsWith("https://"))) return img;
  if (img instanceof Blob) return URL.createObjectURL(img);
  if (typeof img === "string") return `${FILE_BASE_URL}${img}`;
  return `${FILE_BASE_URL}/images/default.png`;
};

const fetchAds = async () => {
  const url = `${API_BASE_URL}/advertisments`;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    ads.value = data.map((ad) => ({
      ...ad,
      image: resolveImage(ad.image),
    }));
  } catch (err) {
    adsError.value = "Failed to load ads.";
    console.error("fetchAds error:", err);
  }
};

const handleImageError = (e) => {
  const img = e.target;
  if (img.src.includes("default.png")) {
    img.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMjQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSIjOTk5Ij5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=";
    return;
  }
  img.src = `${FILE_BASE_URL}/images/default.png`;
  img.onerror = null;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const openPublishModal = () => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const token = localStorage.getItem('token');

  if (!user || !token) {
    Swal.fire({
      icon: 'warning',
      title: 'Login Required',
      text: 'Please log in to publish an advertisement.',
      confirmButtonColor: '#BF3131',
      showCancelButton: true,
      confirmButtonText: 'Go to Login',
      cancelButtonText: 'Cancel'
    }).then(result => {
      if (result.isConfirmed) window.location.href = '/login';
    });
    return;
  }
  showAdForm.value = true;
};

onMounted(fetchAds);
</script>

<style scoped>
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>